import type { IncomingMessage, ServerResponse } from 'http';

import App, { AppInitialProps, AppProps } from 'next/app';

import { getDefaultLocale, Locale } from '../core/i18n/Locale';
import { getIntlMessages, TranslationMessages } from '../core/i18n/Messages';
import { PageProps } from '../core/Page';
import { getSiteFromContext } from '../core/Site/Site';
import { isUserConnected } from '../partials/shared/connected';
import { AppProviders } from '../shared/providers/AppProviders';
import { getGTMId } from '../shared/utilities/GTM/GTM';

import '../styles/GlobalStyle';

type Props = {
    appVersion: string;
    defaultLocale: Locale;
    gtmId: string;
    isConnectedOnLoad: boolean;
    intlMessages: TranslationMessages;
    locale: Locale;
};

interface ExtendedAppProps extends AppProps<PageProps>, Props {}

/**
 * This function is executed both SERVER & CLIENT side.
 */
function ExtendedApp(props: ExtendedAppProps) {
    const { Component, gtmId, intlMessages, defaultLocale, locale, pageProps } = props;
    const errorPageData = {
        gtm: {
            context: 'context-error',
        },
    };
    const { gtm, seoContext, enableGoogleOneTap } = pageProps.data || errorPageData;

    return (
        <AppProviders
            locale={locale}
            defaultLocale={defaultLocale}
            seoContext={seoContext}
            isConnectedOnLoad={props.isConnectedOnLoad}
            intlMessages={intlMessages}
            mapToken={pageProps.mapToken}
            gtmId={gtmId}
            gtmData={gtm}
            enableGoogleOneTap={enableGoogleOneTap}
            user={null}
        >
            <Component {...pageProps} />
        </AppProviders>
    );
}

interface ExtendedAppInitialProps extends AppInitialProps<PageProps>, Props {}

const getInitialProps: typeof App.getInitialProps = async (
    appContext,
): Promise<ExtendedAppInitialProps> => {
    const { ctx } = appContext;
    const { req, res } = ctx;

    const defaultLocale = getDefaultLocale();
    const currentSite = getSiteFromContext(ctx);
    const { locale } = currentSite;

    const [intlMessages, appProps] = await Promise.all([
        getIntlMessages(locale),
        App.getInitialProps(appContext),
    ]);
    const isUserConnectedOnLoad = isUserConnected(
        req as IncomingMessage,
        res as ServerResponse<IncomingMessage>,
    );

    return {
        ...appProps,
        appVersion: process.env.NEXT_PUBLIC_APP_VERSION ?? 'unknown',
        isConnectedOnLoad: isUserConnectedOnLoad,
        gtmId: getGTMId(),
        intlMessages,
        locale,
        defaultLocale,
    };
};

ExtendedApp.getInitialProps = getInitialProps;

export default ExtendedApp;
